import { pantryColors, pantryTypography } from '@pantry/ui'
import React from 'react'

const p = pantryColors
const F = pantryTypography.fontFamily

export const COLORS = {
  accent: '#2563EB',
  accentSoft: 'rgba(37,99,235,0.12)',
  accentLight: '#93C5FD',
  border: p.gray[200],
  borderStrong: p.gray[300],
  textPrimary: p.gray[900] ?? '#111827',
  textSecondary: p.gray[700] ?? '#374151',
  textMuted: p.gray[500] ?? '#6B7280',
  surface: p.white,
  surfaceMuted: '#FAFBFC',
  danger: '#DC2626',
  dangerSoft: '#FEF2F2',
  dangerBorder: '#FECACA',
  warningBg: '#FFFBEB',
  warningBorder: '#FCD34D',
  warningText: '#92400E',
}

export const FONT_FAMILY = F

export const inputStyle = (hasError = false): React.CSSProperties => ({
  width: '100%',
  padding: '8px 12px',
  fontSize: 13,
  border: `1px solid ${hasError ? COLORS.danger : COLORS.border}`,
  borderRadius: 8,
  outline: 'none',
  color: COLORS.textPrimary,
  background: COLORS.surface,
  boxSizing: 'border-box',
  fontFamily: F,
  transition: 'border-color .15s, box-shadow .15s',
})

export const selectStyle = (hasError = false): React.CSSProperties => ({
  ...inputStyle(hasError),
  appearance: 'none' as const,
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%239CA3AF' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
  paddingRight: 30,
  cursor: 'pointer',
})

export const handleInputFocus = (
  e: React.FocusEvent<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >,
) => {
  e.currentTarget.style.borderColor = COLORS.accent
  e.currentTarget.style.boxShadow = `0 0 0 2px ${COLORS.accentSoft}`
}

export const handleInputBlur =
  (hasError = false) =>
  (
    e: React.FocusEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    e.currentTarget.style.borderColor = hasError ? COLORS.danger : COLORS.border
    e.currentTarget.style.boxShadow = 'none'
  }

interface LabelProps {
  children: React.ReactNode
  required?: boolean
  htmlFor?: string
}

export const Label: React.FC<LabelProps> = ({
  children,
  required,
  htmlFor,
}) => (
  <label
    htmlFor={htmlFor}
    style={{
      display: 'block',
      fontSize: 12,
      fontWeight: 600,
      color: COLORS.textSecondary,
      marginBottom: 4,
      fontFamily: F,
    }}
  >
    {children}
    {required && <span style={{ color: COLORS.danger, marginLeft: 2 }}>*</span>}
  </label>
)

interface FieldErrorProps {
  msg?: string
}

export const FieldError: React.FC<FieldErrorProps> = ({ msg }) =>
  msg ? (
    <div
      style={{
        fontSize: 12,
        color: COLORS.danger,
        marginTop: 4,
        fontFamily: F,
      }}
    >
      {msg}
    </div>
  ) : null

interface ToggleRowProps {
  label: string
  description?: string
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
}

export const ToggleRow: React.FC<ToggleRowProps> = ({
  label,
  description,
  checked,
  onChange,
  disabled,
}) => (
  <div
    onClick={() => {
      if (!disabled) onChange(!checked)
    }}
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 12,
      padding: '12px 14px',
      border: `1px solid ${COLORS.border}`,
      borderRadius: 10,
      background: COLORS.surface,
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'border-color .15s, background .15s',
      opacity: disabled ? 0.6 : 1,
      fontFamily: F,
    }}
  >
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.textPrimary }}>
        {label}
      </div>
      {description && (
        <div
          style={{
            fontSize: 12,
            color: COLORS.textMuted,
            marginTop: 2,
            lineHeight: 1.45,
          }}
        >
          {description}
        </div>
      )}
    </div>
    <div
      role="switch"
      aria-checked={checked}
      style={{
        width: 36,
        height: 20,
        borderRadius: 999,
        background: checked ? COLORS.accent : COLORS.borderStrong,
        position: 'relative',
        flexShrink: 0,
        transition: 'background .15s',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 2,
          left: checked ? 18 : 2,
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: COLORS.surface,
          boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
          transition: 'left .15s',
        }}
      />
    </div>
  </div>
)

interface SectionLabelProps {
  children: React.ReactNode
  style?: React.CSSProperties
}

export const SectionLabel: React.FC<SectionLabelProps> = ({
  children,
  style,
}) => (
  <div
    style={{
      fontSize: 10.5,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '.06em',
      color: COLORS.textMuted,
      paddingTop: 4,
      fontFamily: F,
      ...style,
    }}
  >
    {children}
  </div>
)
